import apiClient from './client'
import type {
  Feed,
  CreateFeedRequest,
  UpdateFeedRequest,
  FeedExecution,
  ApiResponse,
  PaginatedResponse,
} from '@/types/models'
import {
  adaptListResponse,
  adaptFeedResponse,
  adaptFeedRequest,
} from '@/lib/utils/api-adapter'

/**
 * Feed API endpoints
 */
export const feedsApi = {
  /**
   * List all feeds for the current user
   */
  async list(params?: {
    limit?: number
    offset?: number
    enabled?: boolean
  }): Promise<PaginatedResponse<Feed>> {
    const response = await apiClient.get<any>('/api/v1/feeds', { params })
    const adapted = adaptListResponse<any>(response, 'feeds')
    return {
      ...adapted,
      data: adapted.data.map(adaptFeedResponse),
    }
  },

  /**
   * Get a single feed by ID
   */
  async get(id: string): Promise<ApiResponse<Feed>> {
    const response = await apiClient.get<any>(`/api/v1/feeds/${id}`)
    return {
      data: adaptFeedResponse(response),
    }
  },

  /**
   * Create a new feed
   */
  async create(data: CreateFeedRequest): Promise<ApiResponse<Feed>> {
    const apiRequest = adaptFeedRequest(data)
    const response = await apiClient.post<any>('/api/v1/feeds', apiRequest)
    return {
      data: adaptFeedResponse(response),
    }
  },

  /**
   * Update an existing feed
   */
  async update(
    id: string,
    data: UpdateFeedRequest
  ): Promise<ApiResponse<Feed>> {
    const apiRequest = adaptFeedRequest(data)
    const response = await apiClient.put<any>(`/api/v1/feeds/${id}`, apiRequest)
    return {
      data: adaptFeedResponse(response),
    }
  },

  /**
   * Delete a feed
   */
  async delete(id: string): Promise<void> {
    return apiClient.delete(`/api/v1/feeds/${id}`)
  },


  /**
   * Get feed execution history
   */
  async getExecutions(
    id: string,
    params?: { limit?: number; offset?: number }
  ): Promise<PaginatedResponse<FeedExecution>> {
    return apiClient.get<PaginatedResponse<FeedExecution>>(
      `/api/v1/feeds/${id}/executions`,
      { params }
    )
  },

  /**
   * Enable a feed
   */
  async enable(id: string): Promise<ApiResponse<Feed>> {
    const response = await apiClient.post<any>(`/api/v1/feeds/${id}/enable`)
    return {
      data: adaptFeedResponse(response),
    }
  },

  /**
   * Disable a feed
   */
  async disable(id: string): Promise<ApiResponse<Feed>> {
    const response = await apiClient.post<any>(`/api/v1/feeds/${id}/disable`)
    return {
      data: adaptFeedResponse(response),
    }
  },

  /**
   * Trigger immediate feed fetch (bypassing schedule)
   */
  async fetchNow(id: string): Promise<ApiResponse<{ message: string; feed_id: string }>> {
    return apiClient.post(`/api/v1/feeds/${id}/fetch`)
  },

  /**
   * Set temporary refresh interval override
   */
  async setOverride(
    id: string,
    data: { refresh_interval: string; duration_minutes: number }
  ): Promise<ApiResponse<{ message: string; refresh_interval: string; expires_at: string }>> {
    return apiClient.post(`/api/v1/feeds/${id}/override`, data)
  },

  /**
   * Clear refresh interval override
   */
  async clearOverride(id: string): Promise<ApiResponse<{ message: string }>> {
    return apiClient.delete(`/api/v1/feeds/${id}/override`)
  },
}

export default feedsApi
